import {
  MapPin,
  Calendar,
  Users,
  CreditCard,
  BarChart3,
  Navigation,
} from "lucide-react";
import { useAppSelector } from "../../store/hooks";
import { selectUser } from "../../store/slices/authSlice";

export default function TravelToursDashboard() {
  const user = useAppSelector(selectUser);

  const stats = [
    {
      title: "Total Tours",
      value: "0",
      change: "0%",
      changeType: "neutral",
      icon: MapPin,
      color: "bg-purple-500",
    },
    {
      title: "Active Bookings",
      value: "0",
      change: "0%",
      changeType: "neutral",
      icon: Calendar,
      color: "bg-emerald-500",
    },
    {
      title: "Total Travelers",
      value: "0",
      change: "0%",
      changeType: "neutral",
      icon: Users,
      color: "bg-blue-500",
    },
    {
      title: "Revenue",
      value: "$0",
      change: "0%",
      changeType: "neutral",
      icon: CreditCard,
      color: "bg-orange-500",
    },
  ];

  const quickActions = [
    {
      title: "Manage Tours",
      icon: MapPin,
      color: "bg-gray-100 text-gray-400",
      available: false,
    },
    {
      title: "Bookings",
      icon: Calendar,
      color: "bg-gray-100 text-gray-400",
      available: false,
    },
    {
      title: "Transportation",
      icon: Navigation,
      color: "bg-gray-100 text-gray-400",
      available: false,
    },
    {
      title: "Analytics",
      icon: BarChart3,
      color: "bg-gray-100 text-gray-400",
      available: false,
    },
  ];

  return (
    <div className="p-6 space-y-6">
      {/* Welcome Section */}
      <div className="p-6 text-white bg-gradient-to-r from-purple-500 to-purple-600 rounded-xl">
        <h1 className="mb-2 text-2xl font-bold">
          Welcome to Travel & Tours Admin, {user?.name || user?.email || "Admin"}! ✈️
        </h1>
        <p className="text-purple-100">
          Manage tours, transportation, and logistics operations from here.
        </p>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-4">
        {stats.map((stat, index) => (
          <div
            key={index}
            className="p-6 bg-white rounded-xl border shadow-sm border-gray-200/50"
          >
            <div className="flex justify-between items-center">
              <div>
                <p className="text-sm font-medium text-gray-600">{stat.title}</p>
                <p className="mt-1 text-2xl font-bold text-gray-900">{stat.value}</p>
              </div>
              <div className={`p-3 rounded-lg ${stat.color}`}>
                <stat.icon size={24} className="text-white" />
              </div>
            </div>
            <div className="flex items-center mt-4">
              <span
                className={`text-sm font-medium ${
                  stat.changeType === "positive"
                    ? "text-emerald-600"
                    : stat.changeType === "negative"
                    ? "text-red-600"
                    : "text-gray-500"
                }`}
              >
                {stat.change}
              </span>
              <span className="ml-2 text-sm text-gray-500">
                {stat.changeType === "neutral" ? "no change" : "from last month"}
              </span>
            </div>
          </div>
        ))}
      </div>

      {/* Quick Actions */}
      <div className="p-6 bg-white rounded-xl border shadow-sm border-gray-200/50">
        <h2 className="mb-4 text-lg font-semibold text-gray-900">Quick Actions</h2>
        <div className="grid grid-cols-2 gap-4 md:grid-cols-4">
          {quickActions.map((action, index) => (
            <button
              key={index}
              onClick={action.onClick}
              disabled={!action.available}
              className={`flex flex-col items-center p-4 rounded-lg border transition-colors ${
                action.available
                  ? "border-gray-200 hover:border-purple-300 hover:bg-purple-50 cursor-pointer"
                  : "border-gray-100 cursor-not-allowed opacity-60"
              }`}
            >
              <div className={`p-3 rounded-lg ${action.color} mb-3`}>
                <action.icon size={24} />
              </div>
              <span
                className={`text-sm font-medium ${
                  action.available ? "text-gray-700" : "text-gray-400"
                }`}
              >
                {action.title}
                {!action.available && " (Coming Soon)"}
              </span>
            </button>
          ))}
        </div>
      </div>

      {/* Recent Activity */}
      <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
        <div className="p-6 bg-white rounded-xl border shadow-sm border-gray-200/50">
          <h2 className="mb-4 text-lg font-semibold text-gray-900">Recent Bookings</h2>
          <div className="space-y-3">
            <div className="flex justify-center items-center p-8 text-center">
              <div>
                <Calendar size={48} className="mx-auto mb-4 text-gray-300" />
                <p className="text-gray-500">No bookings yet</p>
                <p className="text-sm text-gray-400">
                  Bookings will appear here once they are created
                </p>
              </div>
            </div>
          </div>
        </div>

        <div className="p-6 bg-white rounded-xl border shadow-sm border-gray-200/50">
          <h2 className="mb-4 text-lg font-semibold text-gray-900">System Status</h2>
          <div className="space-y-4">
            <div className="flex justify-between items-center">
              <span className="text-sm font-medium text-gray-400">Tour Management</span>
              <span className="text-sm font-medium text-gray-400">Coming Soon</span>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-sm font-medium text-gray-400">Transportation</span>
              <span className="text-sm font-medium text-gray-400">Coming Soon</span>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-sm font-medium text-gray-400">Logistics</span>
              <span className="text-sm font-medium text-gray-400">Coming Soon</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

